// Each view's props, as zod: exactly the tool's answer (its structuredContent), so a view draws the same
// in the office app and in an AI tool's chat (MCP Apps). These are what the views read, not copies of the
// actions' schemas (ui-web may not import modules): services/api's ui-contract test runs each real tool
// and parses its answer with these, so a drift between a tool and its view fails CI. Extra fields in an
// answer are fine (z.object, not strict); a missing or retyped field isn't.
import { z } from 'zod';
import { PricebookArtProps } from './pricebook-art-schemas.ts';

const Id = z.string().min(1);
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

/** customers.findByPhone: who's behind a number. */
export const CallerMatchesProps = z.object({
  phone: z.string().nullable(),
  matches: z.array(
    z.object({ customerId: Id, customerName: z.string(), contactId: Id, contactName: z.string() }),
  ),
});
export type CallerMatchesProps = z.infer<typeof CallerMatchesProps>;

/** customers.glance: a customer at a glance, for a call. */
export const CustomerGlanceProps = z.object({
  customerId: Id,
  name: z.string(),
  address: z.object({ line1: z.string(), city: z.string() }).nullable(),
  since: LocalDate,
  jobs: z.object({ count: z.number().int(), lastVisitOn: LocalDate.nullable() }),
  billedCents: z.number().int().nullable(),
  membership: z.object({ name: z.string(), endsOn: LocalDate, autoRenew: z.boolean() }).nullable(),
  openEstimates: z.object({
    count: z.number().int(),
    listed: z.array(z.object({ id: Id, number: z.string(), title: z.string(), jobId: Id })),
  }),
  equipment: z.object({
    count: z.number().int(),
    listed: z.array(
      z.object({
        id: Id,
        kind: z.string(),
        make: z.string().nullable(),
        model: z.string().nullable(),
        installedOn: LocalDate.nullable(),
      }),
    ),
  }),
  about: z.string().nullable(),
  notes: z.array(z.object({ body: z.string(), on: LocalDate })),
  siteContact: z.object({ name: z.string(), phone: z.string().nullable() }).nullable(),
  firstCampaign: z.object({ id: Id, name: z.string(), channel: z.string() }).nullable(),
});
export type CustomerGlanceProps = z.infer<typeof CustomerGlanceProps>;

/** approvals.list: what waits for a person's OK (DEC-009: an AI tool's request, an agent's, a price). */
export const ApprovalListProps = z.object({
  /** The company's time zone, for each request's time (QA-939). */
  timeZone: z.string(),
  approvals: z.array(
    z.object({
      id: Id,
      action: z.string(),
      actionTitle: z.string(),
      labels: z.record(z.string(), z.string()).optional(),
      agent: z.string(),
      explanation: z.string().nullable(),
      status: z.enum(['pending', 'approved', 'declined', 'expired']),
      createdAt: z.string(),
      expiresAt: z.string(),
      cannotDecide: z.string().nullable(),
      canTrustConnection: z.boolean(),
    }),
  ),
});
export type ApprovalListProps = z.infer<typeof ApprovalListProps>;

/** gp.pace: the month's revenue against the goal, by working day. */
export const GoalPaceProps = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string(),
  status: z.enum(['no_target', 'too_early', 'ahead', 'behind']),
  workingDays: z.number().int(),
  workingDaysElapsed: z.number().int(),
  targetCents: z.number().int().nullable(),
  expectedCents: z.number().int(),
  /** In by the last finished day, over months with a goal: what the pace compares with expected. */
  actualCents: z.number().int(),
  /** Everything in so far, through today, goal or not (QA-683): what "so far" says. */
  totalCents: z.number().int(),
  deltaCents: z.number().int().nullable(),
  projectedCents: z.number().int().nullable(),
  provisionalCents: z.number().int(),
  jobCount: z.number().int(),
});
export type GoalPaceProps = z.infer<typeof GoalPaceProps>;

const AGING = ['current', 'days_1_30', 'days_31_60', 'days_61_90', 'days_over_90'] as const;
/** receivables.aging: who owes what, by how late. */
export const AgingProps = z.object({
  asOf: z.string(),
  rows: z.array(
    z.object({
      invoiceId: Id,
      number: z.string(),
      customerId: Id,
      customerName: z.string(),
      dueOn: z.string(),
      daysPastDue: z.number().int(),
      bucket: z.enum(AGING),
      balanceCents: z.number().int(),
      onHold: z.boolean(),
    }),
  ),
  buckets: z.object(
    Object.fromEntries(AGING.map((b) => [b, z.number().int()])) as Record<
      (typeof AGING)[number],
      z.ZodNumber
    >,
  ),
  openInvoicesCents: z.number().int(),
  unappliedCreditsCents: z.number().int(),
});
export type AgingProps = z.infer<typeof AgingProps>;
export const AGING_BUCKETS = AGING;

/** agents.activity: what Flowology's agents did on a day, newest first. */
export const AgentActivityProps = z.object({
  date: z.string(),
  /** The company's time zone, for each step's time (QA-939). */
  timeZone: z.string(),
  steps: z.array(
    z.object({
      runId: Id,
      n: z.number().int(),
      agentKey: z.string(),
      at: z.string(),
      action: z.string().nullable(),
      actionTitle: z.string().nullable(),
      mode: z.enum(['automatic', 'asked', 'refused', 'failed']),
      outcome: z.string(),
      approvalId: z.string().nullable(),
      task: z.string(),
      /** What a person decided on what it asked (QA-858); null while it waits. */
      decision: z
        .object({
          status: z.enum(['approved', 'declined', 'expired']),
          by: z.string().nullable(),
          at: z.string().nullable(),
          note: z.string().nullable(),
        })
        .nullable()
        .optional(),
    }),
  ),
});
export type AgentActivityProps = z.infer<typeof AgentActivityProps>;

// ── Home › Ads and calls, and Profit (pages/ux-full components/marketing.js) ──
// Not chat views yet (no TOOL_VIEWS entry): each is an action's answer, or a slice of it, so it can be one.

const Channel = z.string();
const Money = z.number().int();

/** reports.campaigns: one row (by campaign, channel or month), its rates as the server works them out. */
export const CampaignRow = z.object({
  key: z.string().nullable(),
  label: z.string(),
  channel: Channel.nullable(),
  month: z.string().nullable(),
  /** By day or week: the day, or the week's Monday ('YYYY-MM-DD'); by month its 'YYYY-MM'. */
  period: z.string().nullable().optional(),
  calls: z.number().int(),
  /** Of the calls, those from a number that had called in the 7 days before. */
  repeatCalls: z.number().int().optional(),
  leads: z.number().int(),
  unbookedLeads: z.number().int(),
  booked: z.number().int(),
  leadConversionRate: z.number().nullable(),
  callsBooked: z.number().int(),
  callBookingRate: z.number().nullable(),
  jobsCompleted: z.number().int(),
  invoicedJobs: z.number().int(),
  revenueCents: Money,
  averageTicketCents: Money.nullable(),
  /** Only for whoever sees margins (finance.margins.view); absent otherwise. */
  grossProfitCents: Money.nullable().optional(),
  spendCents: Money,
  spendEstimated: z.boolean(),
  costPerLeadCents: Money.nullable(),
  costPerBookedJobCents: Money.nullable(),
  roas: z.number().nullable(),
});
export type CampaignRow = z.infer<typeof CampaignRow>;

/** reports.campaigns' answer. */
export const CampaignsReport = z.object({
  rows: z.array(CampaignRow),
  total: CampaignRow,
  grossProfitNote: z.string().nullable(),
  adSpendNote: z.string().nullable(),
});
export type CampaignsReport = z.infer<typeof CampaignsReport>;

/** How the ads did: reports.campaigns by channel for a period, with the period's words. */
export const AdsSummaryProps = CampaignsReport.extend({
  label: z.string(),
  /** Said under the figures when the period's jobs aren't finished yet ("today"). */
  note: z.string().nullable().optional(),
});
export type AdsSummaryProps = z.infer<typeof AdsSummaryProps>;

/** Over time: reports.campaigns by day or by week, every period of the range a row. */
export const AdsTrendProps = CampaignsReport.extend({
  label: z.string(),
  unit: z.enum(['day', 'week']),
});
export type AdsTrendProps = z.infer<typeof AdsTrendProps>;

/** From call to invoice: reports.campaigns rows (the paid ones are counted). */
export const AdsFunnelProps = z.object({ label: z.string(), rows: z.array(CampaignRow) });
export type AdsFunnelProps = z.infer<typeof AdsFunnelProps>;

/** campaigns.list: one campaign, its channel, whether retired, and its tracking numbers. */
export const CampaignInfo = z.object({
  id: Id,
  name: z.string(),
  channel: Channel,
  active: z.boolean(),
  fromServiceTitan: z.boolean(),
  numbers: z.array(z.object({ phone: z.string(), text: z.string(), label: z.string().nullable() })),
});
/** Campaigns by return: reports.campaigns by campaign, beside campaigns.list. */
export const CampaignTableProps = CampaignsReport.extend({
  label: z.string(),
  campaigns: z.array(CampaignInfo),
});
export type CampaignTableProps = z.infer<typeof CampaignTableProps>;

/** adSpend.list's entries for a range, and the day it's read on (for where the month is heading). */
export const SpendProps = z.object({
  from: LocalDate,
  to: LocalDate,
  today: LocalDate,
  entries: z.array(
    z.object({
      id: Id,
      campaign: z.object({ id: Id, name: z.string() }).nullable(),
      channel: Channel,
      platformCampaign: z.object({ id: z.string().nullable(), name: z.string().nullable() }).nullable(),
      from: LocalDate,
      to: LocalDate,
      amountCents: Money,
      estimated: z.boolean(),
    }),
  ),
});
export type SpendProps = z.infer<typeof SpendProps>;

const ReasonRow = z.object({
  key: z.string().nullable(),
  label: z.string(),
  taken: z.number().int(),
  booked: z.number().int(),
  leads: z.number().int(),
  unbookedLeads: z.number().int(),
  excused: z.number().int(),
  underAMinute: z.number().int(),
  notSet: z.number().int(),
  rate: z.number().nullable(),
});
/** reports.callReasons: calls taken by the reason the office set, and the missed ones. */
export const CallReasonsProps = z.object({
  label: z.string(),
  rows: z.array(ReasonRow),
  total: ReasonRow,
  missed: z.number().int(),
});
export type CallReasonsProps = z.infer<typeof CallReasonsProps>;

/** calls.recent's calls (the page picks today's), each with its campaign and what came of it. */
export const CallsBySourceProps = z.object({
  label: z.string(),
  /** The company's time zone, for each call's time. */
  timeZone: z.string(),
  calls: z.array(
    z.object({
      id: Id,
      direction: z.enum(['inbound', 'outbound']),
      from: z.string(),
      to: z.string(),
      status: z.string(),
      startedAt: z.string(),
      outcome: z.string().nullable(),
      reason: z.object({ name: z.string() }).nullable(),
      takenBy: z.string().nullable(),
      textedBackAt: z.string().nullable(),
      campaign: z.object({ id: Id, name: z.string(), channel: Channel }).nullable(),
      trackingLabel: z.string().nullable(),
      customerId: Id.nullable(),
      matches: z.array(z.object({ customerId: Id, customerName: z.string() })),
    }),
  ),
});
export type CallsBySourceProps = z.infer<typeof CallsBySourceProps>;

const ConversionKind = z.enum(['lead', 'quoted', 'booked', 'completed']);
/** adConversions.list: what was sent to Google Ads (or waits, or was kept back), newest first, a page at a time. */
export const ConversionsProps = z.object({
  timeZone: z.string(),
  conversions: z.array(
    z.object({
      id: Id,
      /** The job; a lead call's job once it booked one, else null. */
      job: z.object({ id: Id, number: z.string() }).nullable(),
      callId: Id.nullable(),
      kind: ConversionKind,
      method: z.enum(['click', 'call']),
      conversionAt: z.string(),
      valueCents: Money.nullable(),
      status: z.enum(['queued', 'sent', 'refused', 'skipped']),
      attempts: z.number().int(),
      lastError: z.string().nullable(),
      sentAt: z.string().nullable(),
      /** What Google holds now, after any restatement; and when it was taken back. */
      sentValueCents: Money.nullable(),
      retractedAt: z.string().nullable(),
    }),
  ),
  /** How many there are, whatever the page shows; and the next page's cursor. */
  total: z.number().int().optional(),
  next: z.string().nullable().optional(),
});
export type ConversionsProps = z.infer<typeof ConversionsProps>;

const ActionPair = z.object({ click: z.string().nullable(), call: z.string().nullable() });
/** adAccounts.status: each ad account's connection and last spend pull, the conversions' switch and map, the week's sending, and what stops the numbers. */
export const AdsStatusProps = z.object({
  timeZone: z.string(),
  accounts: z.array(
    z.object({
      platform: z.enum(['google_ads', 'microsoft_ads']),
      connected: z.boolean(),
      test: z.boolean(),
      customerId: z.string().nullable(),
      lastOkDay: LocalDate.nullable(),
      lastOkAt: z.string().nullable(),
      lastAttemptAt: z.string().nullable(),
      lastError: z.string().nullable(),
      failing: z.boolean(),
    }),
  ),
  conversions: z.object({
    on: z.boolean(),
    from: LocalDate.nullable(),
    actionsFrom: z.enum(['setting', 'account', 'none']),
    actions: z.object({ lead: ActionPair, quoted: ActionPair, booked: ActionPair, completed: ActionPair }),
    shared: z.array(z.string()),
    week: z.object({
      queued: z.number().int(),
      sent: z.number().int(),
      refused: z.number().int(),
      skipped: z.number().int(),
    }),
    waitingOverADay: z.number().int(),
    lastSentAt: z.string().nullable(),
    refusals: z.array(z.object({ at: z.string(), kind: ConversionKind, error: z.string() })),
    adjustmentsWaiting: z.number().int(),
  }),
  notices: z.array(z.object({ key: z.string(), text: z.string() })),
});
export type AdsStatusProps = z.infer<typeof AdsStatusProps>;

/** adChanges.list's change: what to change in which ad platform, why with its figures, and where it stands. */
export const AdChangeRow = z.object({
  id: Id,
  platform: z.enum(['google_ads', 'microsoft_ads', 'lsa']),
  kind: z.enum(['budget', 'pause', 'keyword', 'location']),
  campaign: z.object({ id: Id, name: z.string() }).nullable(),
  /** Where in the platform: a campaign, an ad group, all search campaigns. */
  target: z.string(),
  title: z.string(),
  why: z.string(),
  effect: z.string(),
  figures: z.array(
    z.object({ label: z.string(), value: z.number().int(), unit: z.enum(['money', 'count']) }),
  ),
  preparedBy: z.object({ name: z.string(), via: z.enum(['review', 'person', 'ai_tool', 'agent']) }),
  preparedAt: z.string(),
  status: z.enum(['waiting', 'approved', 'dismissed', 'done']),
  decided: z.object({ by: z.string(), at: z.string(), note: z.string().nullable() }).nullable(),
  done: z.object({ by: z.string(), at: z.string() }).nullable(),
});
export type AdChangeRow = z.infer<typeof AdChangeRow>;

/** One suggested ad change, read in the company's time zone. */
export const AdChangeProps = z.object({ timeZone: z.string(), change: AdChangeRow });
export type AdChangeProps = z.infer<typeof AdChangeProps>;

/** adChanges.list: the queue, newest first, and how many wait whatever the page shows. */
export const AdChangesProps = z.object({
  timeZone: z.string(),
  changes: z.array(AdChangeRow),
  waiting: z.number().int(),
});
export type AdChangesProps = z.infer<typeof AdChangesProps>;

/** reports.jobCosting: one job's revenue, cost of sales, paid labour and gross profit. */
export const CostRow = z.object({
  jobId: Id,
  jobNumber: z.string(),
  revenueCents: Money,
  costOfSalesCents: Money,
  grossProfitCents: Money,
  marginPct: z.number().nullable(),
  pendingPurchasesCents: Money,
  untrackedMaterialsCents: Money,
  /** Paid labour with its burden: the job's total, never a person's rate. */
  labourCents: Money,
  labourMinutes: z.number().int(),
  /** Who has hours on the job and no pay set: those hours aren't in labourCents. */
  noPayRate: z.array(z.object({ staffId: Id, name: z.string(), minutes: z.number().int() })),
  /** Each person's paid minutes on the job, adding up to labourMinutes: what By technician counts. */
  labourBy: z.array(z.object({ staffId: Id, name: z.string(), minutes: z.number().int() })),
  /** Whether any material, purchase or receipt was ever logged on the job. */
  materialsLogged: z.boolean(),
});
export type CostRow = z.infer<typeof CostRow>;
/** reports.jobCosting's answer for a range. */
export const JobCosting = z.object({
  rows: z.array(CostRow),
  revenueCents: Money,
  costOfSalesCents: Money,
  grossProfitCents: Money,
  pendingPurchasesCents: Money,
  labourCents: Money,
  labourMinutes: z.number().int(),
  /** costing.labourBurden: the share of pay added for the employer's costs, in percent. */
  labourBurdenPct: z.number(),
  /** The margin we aim for (estimates.warnMarginPct), in percent; null when off. */
  targetMarginPct: z.number().nullable(),
  source: z.enum(['ledger', 'direct', 'both']),
});
export type JobCosting = z.infer<typeof JobCosting>;
/** A day's gross profit: reports.jobCosting for it, with the day's words. */
export const ProfitDayProps = JobCosting.extend({ label: z.string() });
export type ProfitDayProps = z.infer<typeof ProfitDayProps>;

/** jobs.get, the parts the job rows read: who it was for, what it was, who was on it, their time. */
export const CostedJob = z.object({
  id: Id,
  number: z.string(),
  summary: z.string(),
  customerName: z.string(),
  appointments: z.array(
    z.object({
      status: z.string(),
      leadTechId: Id.nullable(),
      techs: z.array(z.object({ id: Id, name: z.string() })),
    }),
  ),
  timeLogged: z.array(z.object({ staffId: Id, name: z.string(), workMinutes: z.number().int() })),
});
export type CostedJob = z.infer<typeof CostedJob>;
/** Job by job, or technician by technician: reports.jobCosting beside each job's own record. */
export const JobMarginsProps = z.object({ label: z.string(), costing: JobCosting, jobs: z.array(CostedJob) });
export type JobMarginsProps = z.infer<typeof JobMarginsProps>;

/** gp.huddle, the slice the profit page reads: each day and the targets the office plans by. */
export const HuddleDaysProps = z.object({
  days: z.array(
    z.object({
      date: LocalDate,
      working: z.boolean(),
      closed: z.string().nullable(),
      revenueCents: Money,
      provisionalCents: Money,
      jobs: z.number().int(),
      sold: z.number().int(),
      note: z.string().nullable(),
      today: z.boolean(),
      future: z.boolean(),
    }),
  ),
  targets: z.object({ plannedTechs: z.number().nullable(), callsPerTechDay: z.number().nullable() }),
  budgeted: z.object({ conversionPct: z.number().nullable(), avgTicketCents: Money.nullable() }),
});
export type HuddleDaysProps = z.infer<typeof HuddleDaysProps>;

/**
 * Which view draws which tool's answer, by action name (dotted, as the executor names it; the MCP page
 * bridge converts from the tool's underscored name). Each such action declares view: 'card' | 'board'
 * (Drew's side); services/api's ui-contract test checks the two lists match both ways.
 */
export const TOOL_VIEWS = {
  'customers.findByPhone': { view: 'CallerMatches', page: 'card', props: CallerMatchesProps },
  'customers.glance': { view: 'CustomerGlance', page: 'card', props: CustomerGlanceProps },
  'approvals.list': { view: 'ApprovalList', page: 'card', props: ApprovalListProps },
  'gp.pace': { view: 'PaceView', page: 'card', props: GoalPaceProps },
  'receivables.aging': { view: 'Aging', page: 'card', props: AgingProps },
  'agents.activity': { view: 'AgentActivity', page: 'card', props: AgentActivityProps },
  'pricebook.art.get': { view: 'PricebookArt', page: 'card', props: PricebookArtProps }, // BL-109
} as const;
export type ToolName = keyof typeof TOOL_VIEWS;
